/**
 * RssFeedsManagementPage — Admin page for managing RSS feed sources.
 * Feeds are synced periodically and imported as social feed posts.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Rss,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Clock,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import toast from 'react-hot-toast';

import {
  useRssFeeds,
  useCreateRssFeed,
  useUpdateRssFeed,
  useDeleteRssFeed,
  useSyncRssFeeds,
  useResyncRssFeed,
  usePurgeRssFeedPosts,
} from '../hooks/useRssFeeds';
import type { RssFeed } from '../hooks/useRssFeeds';
import {
  ADMIN_COLORS,
  staggerContainer,
  staggerItem,
  triggerHapticFeedback,
} from '../utils/adminAnimations';
import {
  PageHeader,
  StatCard,
  CardSkeleton,
  EmptyState,
  ErrorState,
  CreateButton,
  StatusDot,
} from '@/components/ui';
import { useRTL } from '@/hooks/useRTL';
import { FormModal, MODAL_COLORS } from '@/components/modal';
import { useActiveRoles } from '../hooks/useRoles';
import { RssFeedDetailModal } from './RssFeedDetailModal';

function formatSyncTime(iso: string | null, t: (key: string) => string): string {
  if (!iso) return t('rssAdmin.neverSynced');
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.floor((now - d.getTime()) / 60000);
  if (diffMin < 1) return t('rssAdmin.justNow');
  if (diffMin < 60) return `${diffMin}m`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h`;
  return d.toLocaleDateString();
}

// ═══════════════════════════════════════════════════════════════
// Feed Form Modal (Create / Edit)
// ═══════════════════════════════════════════════════════════════
function FeedFormModal({
  isOpen,
  onClose,
  editData,
}: {
  isOpen: boolean;
  onClose: () => void;
  editData: RssFeed | null;
}) {
  const { t } = useTranslation();
  const createMutation = useCreateRssFeed();
  const updateMutation = useUpdateRssFeed();

  const { data: roles } = useActiveRoles();

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [iconUrl, setIconUrl] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [isBreaking, setIsBreaking] = useState(false);
  const [maxPostsPerHour, setMaxPostsPerHour] = useState(5);
  const [notifyTier, setNotifyTier] = useState('none');
  const [notifyKeywords, setNotifyKeywords] = useState('');
  const [notifyRoleIds, setNotifyRoleIds] = useState<string[]>([]);

  // Reset form when modal opens or editData changes
  useEffect(() => {
    if (isOpen) {
      setName(editData?.name || '');
      setUrl(editData?.url || '');
      setIconUrl(editData?.iconUrl || '');
      setIsEnabled(editData?.isEnabled ?? true);
      setIsBreaking(editData?.isBreaking ?? false);
      setMaxPostsPerHour(editData?.maxPostsPerHour ?? 5);
      setNotifyTier(editData?.notifyTier || 'none');
      setNotifyKeywords(editData?.notifyKeywords?.join(', ') || '');
      setNotifyRoleIds(editData?.notifyRoleIds || []);
    }
  }, [isOpen, editData]);

  const handleSubmit = async () => {
    if (!name.trim() || !url.trim()) {
      toast.error(t('rssAdmin.nameUrlRequired'));
      return;
    }

    const keywords = notifyKeywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const payload = {
      name,
      url,
      iconUrl: iconUrl || null,
      isEnabled,
      isBreaking,
      maxPostsPerHour,
      notifyTier,
      notifyKeywords: keywords,
      notifyRoleIds,
    };

    try {
      if (editData) {
        await updateMutation.mutateAsync({ id: editData.id, data: payload });
        toast.success(t('rssAdmin.feedUpdated'));
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(t('rssAdmin.feedCreated'));
      }
      onClose();
    } catch {
      toast.error(editData ? t('rssAdmin.updateError') : t('rssAdmin.createError'));
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const isFormValid = !!name.trim() && !!url.trim();

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={editData ? t('rssAdmin.editFeed') : t('rssAdmin.addFeed')}
      onSubmit={handleSubmit}
      isPending={isPending}
      isFormValid={isFormValid}
      submitLabel={editData ? t('common.save') : t('rssAdmin.addFeed')}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {t('rssAdmin.feedName')}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Vidnight Blog"
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {t('rssAdmin.feedUrl')}
          </label>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/feed.xml"
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
            dir="ltr"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {t('rssAdmin.iconUrl')} <span className="text-slate-400">({t('feed.optional')})</span>
          </label>
          <input
            type="url"
            value={iconUrl}
            onChange={(e) => setIconUrl(e.target.value)}
            placeholder="https://example.com/logo.png"
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
            dir="ltr"
          />
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isEnabled}
            onChange={(e) => setIsEnabled(e.target.checked)}
            className="w-5 h-5"
          />
          <span className="text-sm font-medium text-slate-700">{t('rssAdmin.enabled')}</span>
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isBreaking}
            onChange={(e) => setIsBreaking(e.target.checked)}
            className="w-5 h-5"
          />
          <span className="text-sm font-medium text-slate-700">{t('rssAdmin.isBreaking')}</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            {t('rssAdmin.maxPostsPerHour')}
          </label>
          <input
            type="number"
            min={1}
            max={100}
            value={maxPostsPerHour}
            onChange={(e) => setMaxPostsPerHour(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
            style={inputStyle}
          />
          <p className="text-xs text-slate-400 mt-1">{t('rssAdmin.maxPostsPerHourHelp')}</p>
        </div>

        {/* Notification Config */}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <p className="text-sm font-bold text-slate-700">{t('rssAdmin.notificationConfig')}</p>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('rssAdmin.notifyTier')}
            </label>
            <select
              value={notifyTier}
              onChange={(e) => setNotifyTier(e.target.value)}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
              style={inputStyle}
            >
              <option value="none">{t('rssAdmin.tierNone')}</option>
              <option value="immediate">{t('rssAdmin.tierImmediate')}</option>
              <option value="daily">{t('rssAdmin.tierDaily')}</option>
              <option value="weekly">{t('rssAdmin.tierWeekly')}</option>
            </select>
          </div>

          {notifyTier === 'immediate' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('rssAdmin.notifyKeywords')}
              </label>
              <input
                type="text"
                value={notifyKeywords}
                onChange={(e) => setNotifyKeywords(e.target.value)}
                placeholder="safety, alert, urgent"
                className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
                style={inputStyle}
                dir="ltr"
              />
              <p className="text-xs text-slate-400 mt-1">{t('rssAdmin.keywordsHelp')}</p>
            </div>
          )}

          {(notifyTier === 'immediate' || notifyTier === 'daily') && roles && roles.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                {t('rssAdmin.notifyRoles')}
              </label>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {roles.map((role: { id: string; name: string; code: string }) => (
                  <label key={role.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifyRoleIds.includes(role.id)}
                      onChange={(e) => {
                        setNotifyRoleIds((prev) =>
                          e.target.checked
                            ? [...prev, role.id]
                            : prev.filter((id) => id !== role.id)
                        );
                      }}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-slate-700">{role.name}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-1">{t('rssAdmin.rolesHelp')}</p>
            </div>
          )}
        </div>
      </div>
    </FormModal>
  );
}

// ═══════════════════════════════════════════════════════════════
// Main Page
// ═══════════════════════════════════════════════════════════════
export function RssFeedsManagementPage() {
  const { t } = useTranslation();
  const isRTL = useRTL();

  const { data: feeds, isLoading, isError } = useRssFeeds();
  const deleteMutation = useDeleteRssFeed();
  const updateMutation = useUpdateRssFeed();
  const syncMutation = useSyncRssFeeds();
  const resyncMutation = useResyncRssFeed();
  const purgeMutation = usePurgeRssFeedPosts();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editFeed, setEditFeed] = useState<RssFeed | null>(null);
  const [viewFeed, setViewFeed] = useState<RssFeed | null>(null);

  // Keep viewFeed in sync with latest data
  useEffect(() => {
    if (viewFeed && feeds) {
      const updated = feeds.find((f) => f.id === viewFeed.id);
      if (updated) setViewFeed(updated);
    }
  }, [feeds]);

  const totalFeeds = feeds?.length ?? 0;
  const activeFeeds = feeds?.filter((f) => f.isEnabled).length ?? 0;
  const totalRssPosts = feeds?.reduce((sum, f) => sum + f.postCount, 0) ?? 0;
  const lastSynced = feeds
    ?.filter((f) => f.lastSyncAt)
    .sort((a, b) => new Date(b.lastSyncAt!).getTime() - new Date(a.lastSyncAt!).getTime())[0]
    ?.lastSyncAt;

  const handleSyncAll = async () => {
    triggerHapticFeedback();
    try {
      const result = await syncMutation.mutateAsync(undefined);
      toast.success(t('rssAdmin.syncComplete', { count: result.synced ?? 0 }));
    } catch {
      toast.error(t('rssAdmin.syncError'));
    }
  };

  const handleSyncOne = async (feedId: string) => {
    triggerHapticFeedback();
    try {
      const result = await syncMutation.mutateAsync(feedId);
      toast.success(t('rssAdmin.syncComplete', { count: result.imported ?? 0 }));
    } catch {
      toast.error(t('rssAdmin.syncError'));
    }
  };

  const handleToggleEnabled = async (feed: RssFeed) => {
    triggerHapticFeedback();
    try {
      await updateMutation.mutateAsync({
        id: feed.id,
        data: { isEnabled: !feed.isEnabled },
      });
      toast.success(feed.isEnabled ? t('rssAdmin.feedDisabled') : t('rssAdmin.feedEnabled'));
    } catch {
      toast.error(t('rssAdmin.updateError'));
    }
  };

  // Direct handlers (no confirm wrapper) — used by RssFeedDetailModal which handles its own confirmations
  const handleDeleteDirect = async (feed: RssFeed) => {
    try {
      await deleteMutation.mutateAsync(feed.id);
      setViewFeed(null);
      toast.success(t('rssAdmin.feedDeleted'));
    } catch {
      toast.error(t('rssAdmin.deleteError'));
    }
  };

  const handleResyncDirect = async (feed: RssFeed) => {
    try {
      const result = await resyncMutation.mutateAsync(feed.id);
      toast.success(t('rssAdmin.resyncComplete', { deleted: result.deleted, imported: result.imported }));
    } catch {
      toast.error(t('rssAdmin.syncError'));
    }
  };

  const handlePurgePostsDirect = async (feed: RssFeed) => {
    try {
      const result = await purgeMutation.mutateAsync(feed.id);
      toast.success(t('rssAdmin.deletePostsComplete', { count: result.deleted }));
    } catch {
      toast.error(t('rssAdmin.deleteError'));
    }
  };

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="space-y-4 md:space-y-6 pb-24"
    >
      {/* Header */}
      <PageHeader
        icon={<Rss className="w-7 h-7" />}
        title={t('rssAdmin.title')}
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncAll}
              disabled={syncMutation.isPending || totalFeeds === 0}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors hover:bg-slate-50 disabled:opacity-50"
              style={{ borderColor: ADMIN_COLORS.border }}
            >
              <RefreshCw className={`w-4 h-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {t('rssAdmin.syncAll')}
            </button>
            <CreateButton
              label={t('rssAdmin.addFeed')}
              onClick={() => setCreateModalOpen(true)}
            />
          </div>
        }
      />

      {/* Stats */}
      <motion.div variants={staggerItem} className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          icon={<Rss className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('rssAdmin.totalFeeds')}
          value={totalFeeds}
        />
        <StatCard
          icon={<CheckCircle className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('rssAdmin.activeFeeds')}
          value={activeFeeds}
        />
        <div
          className="flex items-center gap-3 p-3 md:p-4 rounded-xl glass-surface"
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            border: `1px solid ${ADMIN_COLORS.border}`,
            boxShadow: ADMIN_COLORS.shadow,
          }}
        >
          <Clock className="w-7 h-7 md:w-8 md:h-8 flex-shrink-0 text-slate-500" />
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">{t('rssAdmin.lastSynced')}</p>
            <p className="text-xl font-bold text-slate-800">{formatSyncTime(lastSynced ?? null, t)}</p>
          </div>
        </div>
        <StatCard
          icon={<FileText className="w-7 h-7 md:w-8 md:h-8" />}
          label={t('rssAdmin.totalPosts')}
          value={totalRssPosts}
        />
      </motion.div>

      {/* Feed List */}
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <CardSkeleton />
            <CardSkeleton />
          </motion.div>
        ) : isError ? (
          <ErrorState message={t('rssAdmin.loadError')} />
        ) : totalFeeds === 0 ? (
          <EmptyState
            icon={<Rss className="w-14 h-14 md:w-16 md:h-16" />}
            message={t('rssAdmin.noFeeds')}
          />
        ) : (
          <motion.div
            key="content"
            className="space-y-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {feeds!.map((feed) => (
              <motion.div
                key={feed.id}
                role="button"
                tabIndex={0}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  triggerHapticFeedback();
                  setViewFeed(feed);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    triggerHapticFeedback();
                    setViewFeed(feed);
                  }
                }}
                className="flex items-center justify-between w-full py-3 ps-4 pe-3 md:py-3 md:ps-5 md:pe-4 rounded-xl text-start cursor-pointer glass-surface"
                style={{
                  backgroundColor: ADMIN_COLORS.surface,
                  border: `1px solid ${ADMIN_COLORS.border}`,
                  boxShadow: ADMIN_COLORS.shadow,
                }}
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full flex-shrink-0 flex items-center justify-center"
                    style={{
                      background: feed.isEnabled
                        ? 'linear-gradient(135deg, #f97316, #ea580c)'
                        : '#d1d5db',
                    }}
                  >
                    <Rss className="w-5 h-5 text-white" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-xs md:text-sm text-neutral-800 truncate max-w-[200px] md:max-w-none">
                      {feed.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-slate-500 truncate max-w-[180px] md:max-w-[300px]" dir="ltr">
                        {feed.url}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      <span>{feed.postCount} {t('rssAdmin.posts')}</span>
                      <span>{formatSyncTime(feed.lastSyncAt, t)}</span>
                      {feed.syncError && (
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <StatusDot
                    active={feed.isEnabled}
                    title={feed.isEnabled ? t('rssAdmin.enabled') : t('rssAdmin.disabled')}
                  />
                  <ChevronRight
                    className="w-5 h-5 text-slate-500"
                    style={{ transform: isRTL ? 'scaleX(-1)' : undefined }}
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create / Edit Form Modal */}
      <FeedFormModal
        isOpen={createModalOpen || !!editFeed}
        onClose={() => {
          setCreateModalOpen(false);
          setEditFeed(null);
        }}
        editData={editFeed}
      />

      {/* View Detail Modal */}
      {viewFeed && (
        <RssFeedDetailModal
          feed={viewFeed}
          onClose={() => setViewFeed(null)}
          onEdit={() => {
            setEditFeed(viewFeed);
          }}
          onDelete={() => handleDeleteDirect(viewFeed)}
          onSync={() => handleSyncOne(viewFeed.id)}
          onResync={() => handleResyncDirect(viewFeed)}
          onPurgePosts={() => handlePurgePostsDirect(viewFeed)}
          onToggleEnabled={() => handleToggleEnabled(viewFeed)}
          isSyncing={syncMutation.isPending}
          isResyncing={resyncMutation.isPending}
          isPurging={purgeMutation.isPending}
          isToggling={updateMutation.isPending}
        />
      )}
    </motion.div>
  );
}
